/*Copyright (c) 2023. Honeywell International, Inc. All rights reserved.
This file contains trade secrets of Honeywell International, Inc. 
No part may be reproduced or transmitted in any form by any means or for any purpose without the express written permission of Honeywell. */

import { Tab, Tabs, InfoIcon, SuccessIcon, NumericBadge } from "@forge/common";
import { useEffect, useRef, useState } from "react";
import {
  ErrorIcons,
  PendingIcon,
} from "../../SharedComponents/Icons/SuccessfilIcon";
import "./SettingsPage.scss";
import { StepperProps } from "../createTenantDetails/CreateTenant";

import { AppDispatch, RootState } from "../../../store";
import { useDispatch, useSelector } from "react-redux";
import {
  getMetaSettingsForProduct,
  getSettingsForTenant,
} from "../../../store/Tenant/tenant-actions";
import Loader from "../../Loader/Loader";
import { ProductSettings } from "./ProductSettings";
import {
  ErrorSettings,
  IErrorProps,
  InputForSettingChangeProps,
} from "../../../models";
import { getEncryptedSecretWithPublicKey } from "./getEncryptedSecretWithPublicKey";
import { tenantActions } from "../../../store/Tenant/tenant-slice";
import { KeyValuePair } from "tailwindcss/types/config";
import { setErrorPopupsusingErrorCode } from "../../TenantUser/setErrorPopupsusingErrorCode";
import { serverErrorClose } from "../../SharedComponents/serverErrorClose";
import ErrorHandlingComponent from "../../SharedComponents/ErrorHandlingComponent";
import updateErrorList from "../../../utils/methods/updateErrorList";
import { checkValidityOfSettingsField } from "./checkValidityOfSettingsField";
import { useOidcAccessToken } from "@axa-fr/react-oidc";
import GetTokenValues from "../../../utils/methods/GetTokenValues";
import { set } from "zod";
import { useTranslation } from "@scuf/localization";
import { localeKeyMap } from "../../../localization/keys";

export const SettingsPage: React.FC<StepperProps> = ({
  stepperState,
  setStepperState,
}) => {
  const [t] = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const [productTabs, setProductTabs] = useState<string[]>([]);
  const [tabContents, setTabContents] = useState<any>();
  const [selectedTabName, setselectedTabName] = useState("");
  const [tabs, setTabs] = useState<any>([]);
  const tabContentsReference = useRef<any>();
  const tabAndErrorCount = useSelector(
    (state: RootState) => state?.cart?.TabErrorMapping
  );

  const updatedTabErrorMapping = useRef<any>();
  useEffect(() => {
    updatedTabErrorMapping.current = tabAndErrorCount;
  }, [tabAndErrorCount]);

  const tokenValues = GetTokenValues();
  let accessTokenPayload =
    tokenValues !== undefined
      ? tokenValues.accessTokenPayload
      : {
          sub: "",
          ext: {
            customer: "",
            tenant_id: "",
          },
        };

  const tokenInfo = {
    customer: accessTokenPayload?.ext?.customer,
    tenant: accessTokenPayload?.ext?.tenant_id,
    userId: accessTokenPayload?.sub,
  };
  // Get Tab names
  // istanbul ignore next
  // Get metaDatasettings for all Tabs
  useEffect(() => {
    if (productTabs?.length === 0) return;

    setIsLoading(true);
    
    // Create all API calls
    const apiCalls = productTabs.map((product: string) => {
      return dispatch(getMetaSettingsForProduct(product)).unwrap();
    });
    
    // Wait for all API calls to complete
    Promise.allSettled(apiCalls)
      .then((results) => {
        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            const product = productTabs[index];
            const metaDataSettings = result.value.settingsMetadata;
            
            const updatedTabContent = tabContentsReference?.current?.map(
              (item: {
                productId: string;
                productName: string;
                settingsMetaData: {
                  settings: any;
                };
              }) => {
                if (item.productId === product) {
                  return {
                    ...item,
                    settingsMetaData: {
                      ...item.settingsMetaData,
                      settings: metaDataSettings,
                    },
                  };
                }
                return item;
              }
            );
            
            tabContentsReference.current = updatedTabContent;
          } else {
            // Handle individual API call errors
            setErrorListGlobal((prevState) =>
              updateErrorList(result.reason, [...prevState])
            );
          }
        });
        
        // Update tabContents once after all calls are processed
        setTabContents(tabContentsReference.current);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [productTabs]);

  // useEffect(() => {
  //   setselectedTabName(productTabs[0]);
  // }, [productTabs]);

  // Get currentSettings for selected Tab and use the currentSettings for other tabs from store
  // istanbul ignore next
  // Get settings for all Tabs
  useEffect(() => {
    setIsLoading(true);

    dispatch(getSettingsForTenant(tokenInfo?.tenant))
      .unwrap()
      .then((responseArray: any) => {
        let tabContents: any[] = [];
        let products: string[] = [];
        responseArray?.map((response: any) => {
          const product = getPrductForTenant(response);
          const productName = response?.productName;
          products.push(product);
          const tabContent = response;
          (tabContents as unknown as any).push({
            currentSettings: tabContent,
            settingsMetaData: {
              // settings: [],
            },
            productId: product,
            productName: productName,
          });
        });

        tabContentsReference.current = tabContents;
        setProductTabs(products);
        setselectedTabName(products[0]);
      })
      .catch((error: any) => {
        setErrorListGlobal((prevState) =>
          updateErrorList(error, [...prevState])
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  ///////////////Error Handling////////////////////////
  const [isLoading, setIsLoading] = useState(false);
  const toastErrors = useRef<IErrorProps[]>([]);
  const [errorListGlobal, setErrorListGlobal] = useState<Array<IErrorProps>>(
    []
  );
  const [openDialogNoURLError, setOpenDialogNoURLError] = useState(false);
  const [openDialogServerError, setOpenDialogServerError] = useState(false);
  const [openToastError, setOpenToastError] = useState(false);

  const onDialogForServerCloseHandler = () => {
    serverErrorClose({
      errorListGlobal,
      setOpenDialogServerError,
      setErrorListGlobal,
    });
  };
  const onDialogForNOURLCloseHandler = () => {
    setOpenDialogNoURLError(false);
  };
  const handleToastBarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenToastError(false);
  };

  // istanbul ignore next
  useEffect(() => {
    setErrorPopupsusingErrorCode({
      errorListGlobal,
      setOpenDialogNoURLError,
      setOpenToastError,
      setOpenDialogServerError,
      openDialogNoURLError,
      openToastError,
      openDialogServerError,
    });
  }, [errorListGlobal]);

  //////////////Error Handling////////////////////////

  const onClickTabChange = (tabName: any) => {
    setselectedTabName(tabName);
  };

  // Set tab contents
  // istanbul ignore next
  useEffect(() => {
    let tabsSet = tabContents?.map((x: any) => {
      return {
        name: x.productId,
        icon: <></>,
        label: x.productName,
        onclick: () => {
          if (x.productId !== selectedTabName) {
            onClickTabChange(x.productId);
          }
        },
        selectedTabName: selectedTabName,
        tab: x.productId,
        tabIcon: <></>,
        metaData: x.settingsMetaData,
        currentSettings: x.currentSettings,
        error: {
          count: 0,
          networkError: false,
          countOfInvalidFields: 0,
          isTabDirty: false,
        },
      };
    });
    setTabs(tabsSet);
  }, [tabContents]);

  //// Items to be lifted up to parent component ////
  const [TabRelatedInfo, setTabRelatedInfo] = useState<any>();

  useEffect(() => {
    let tabRelatedInfo = tabContents?.map((x: any) => {
      return {
        key: x.productId,
        value: x,
      };
    });
    setTabRelatedInfo(tabRelatedInfo);
  }, [tabContents]);

  // On input chnage
  // istanbul ignore next
  // Call back function to update the settings
  const onInputChange = ({
    event,
    setting,
    productId,
    errorMessage,
  }: InputForSettingChangeProps) => {
    let targetValue = event?.target?.value;
    if (event?.target?.checked !== undefined) {
      targetValue = String(event.target.checked);
    }

    const updatedSettings = TabRelatedInfo?.map((x: any) => {
      if (x.key === productId) {
        return {
          ...x,
          value: {
            ...x.value,
            currentSettings: {
              ...x.value.currentSettings,
              settings: x.value.currentSettings.settings?.map(
                (currentSetting: any) => {
                  if (currentSetting.key === setting.key) {
                    // Check if property is secret.If its secret then encrypt the value

                    return {
                      ...currentSetting,
                      value: targetValue,
                      errorMessage: errorMessage,
                    };
                  }
                  return currentSetting;
                }
              ),
            },
          },
        };
      }
      return x;
    });
    // Check that settings validity
    // If valid then update error count
    setTabRelatedInfo(updatedSettings);
  };

  // Update error count in tabs
  // istanbul ignore next
  // Gets calld by lifting the props up to parent component
  const updateErrorCountInTabsHandler = (
    errorCount: number,
    tabName: string
  ) => {
    const tabAndError = { tabName: tabName, errorCount: errorCount };
    //dispatch(tenantActions.updateTabErrorCountMapping(tabAndError as any));
    //setTabs(updatedTabs);
  };
  useEffect(() => {
    const updatedTabs = tabs?.map((x: any) => {
      // Guard clause
      if (updatedTabErrorMapping?.current?.length === 0) {
        return { ...x };
      }

      const errorInTab: KeyValuePair<string, ErrorSettings> | undefined =
        updatedTabErrorMapping?.current?.find(
          (y: KeyValuePair<string, ErrorSettings>) => y.key === x.name
        );

      const errorCount = errorInTab?.value?.errorCount ?? 0;
      const isNetworkErrorInTab = errorInTab?.value?.isNetworkError;
      return {
        ...x,
        tabIcon: getTabIcon(errorCount, isNetworkErrorInTab),
        icon: getTabIcon(errorCount, isNetworkErrorInTab),
      };
    });
    setTabs(updatedTabs);
  }, [tabAndErrorCount]);

  const [checkIfTabHasError, setCheckIfTabHasError] = useState(
    tabs?.find((x: any) => x.error.count > 0)
  );

  // Check if any Tab has error
  // istanbul ignore next
  // Gets calld by lifting the props up to parent component
  const validate = () => {
    const doesAnyTabHasError = updatedTabErrorMapping?.current?.find(
      (x: KeyValuePair<string, ErrorSettings>) => x.value.errorCount > 0
    );
    return !doesAnyTabHasError;
  };

  // TODO: Get changed settings data
  // istanbul ignore next
  // Gets difference of changed settings data to be passed to make API calls
  const getChangedSettingsData = () => {
    // the previous data
    const previousData = tabContentsReference.current;
    // the new data
    const newData = TabRelatedInfo;
    const differenceOfData = newData?.map((x: any) => {
      const previousDataForProduct = previousData.find(
        (y: any) => y.productId === x.key
      );

      const changedSettings = x.value.currentSettings.settings?.filter(
        (setting: any) => {
          let settingValue = setting.value;
          const previousSetting =
            previousDataForProduct.currentSettings.settings.find(
              (y: any) => y.key === setting.key
            );

          if (setting.type === "secret") {
            // Encrypt the value
            settingValue = getEncryptedSecretWithPublicKey(setting.value);
            setting.value = settingValue;
          }
          return settingValue !== previousSetting.value;
        }
      );

      return {
        productId: x.key,
        changedSettings: changedSettings,
        version: x.value.currentSettings.version,
      };
    });
    return differenceOfData;
  };

  const checkAllTabsForErrors = () => {
    const errorCountToUpdate: Array<KeyValuePair<string, number>> = [];
    TabRelatedInfo?.map((x: any) => {
      let errorInTab = 0;
      const tabName = x.key;
      const settings = x.value.currentSettings.settings;

      settings.map((setting: any) => {
        const metaDataSetting = x?.value?.settingsMetaData?.settings?.filter(
          (y: { key: any }) => y?.key === setting?.key
        );
        if (!metaDataSetting) {
          return;
        }
        //const validationRegex = metaDataSetting[0]?.validation;

        const isRequired =
          metaDataSetting[0]?.required === true || setting?.required === true;

        let isError = !checkValidityOfSettingsField(
          setting?.value,
          setting?.errorMessage,
          metaDataSetting[0]?.rules,
          isRequired
        );
        if (
          (setting?.value === "*****" || setting?.value === "***") &&
          setting?.type === "secret"
        ) {
          isError = false;
        }
        if (isError) {
          errorInTab++;
        }
      });
      errorCountToUpdate.push({ key: tabName, value: errorInTab });
    });
    // Push all the errors in store
    errorCountToUpdate.map((errorTabRelation: KeyValuePair<string, number>) => {
      const tabAndError = {
        tabName: errorTabRelation?.key,
        errorCount: errorTabRelation?.value ?? 0,
      };
      // If error count is not same as the one in store , dispatch and update the store
      // Else no need to dispatch as the tab error count is already updated
      // const tabErrorCount= updatedTabErrorMapping?.current?.find(
      //   (x: KeyValuePair<string, ErrorSettings>) =>
      //     x?.key === tabAndError?.tabName
      // )?.value?.errorCount;

      dispatch(tenantActions.updateTabErrorCountMapping(tabAndError as any));
    });
    return errorCountToUpdate;
    // Check if any tab has error
  };

  useEffect(() => {
    setCheckIfTabHasError(tabs?.find((x: any) => x.error.count > 0));
  }, [tabs]);
  const [tabPanel, setTabPanel] = useState<any>();
  const [tabPanelToShow, setTabPanelToShow] = useState(
    tabPanel?.filter((tab: any) => tab.tab === selectedTabName)[0]?.component
  );
  const tabPanelToShowCopy = useRef(tabPanelToShow);
  const currentTabKey = useRef(0);

  // To refresh tab content during 1st time rendering of tabContent as it was coming sometimes and not coming sometimes
  useEffect(() => {
    if (!tabPanel || tabPanel.length === 0 || !selectedTabName) {
      setTabPanelToShow(null);
      return;
    }

    const selectedTabComponent = tabPanel.find(
      (tab: any) => tab.tab === selectedTabName
    )?.component;

    if (selectedTabComponent) {
      setTabPanelToShow(selectedTabComponent);
      currentTabKey.current = currentTabKey.current + 1;
    }
  }, [selectedTabName, tabPanel]);
  // An effect to re-render the component wto show tabPanel 1st time
  useEffect(() => {
    tabPanelToShowCopy.current = tabPanelToShow;
  }, [tabPanelToShow]);

  // Use effect to update tab related info
  // istanbul ignore next
  // Gets calld by lifting the props up to parent component
  useEffect(() => {
    setTheTabPanel();
  }, [TabRelatedInfo]);
  const setTheTabPanel = () => {
    const newTabPanel = TabRelatedInfo?.map((x: any) => {
      return {
        selectedTabName: selectedTabName,
        tab: x.key,
        component: (
          <ProductSettings
            key={x.key + x.value.currentSettings}
            tabName={x.key}
            tabContent={x.value}
            productId={x.key}
            onInputChange={onInputChange}
            updateErrorCountInTabsHandler={updateErrorCountInTabsHandler}
            checkIfTabHasError={checkIfTabHasError}
            validate={validate}
            setStepperState={setStepperState}
            stepperState={stepperState}
            getChangedSettingsData={getChangedSettingsData}
            checkAllTabsForErrors={checkAllTabsForErrors}
          />
        ),
      };
    });
    setTabPanel(newTabPanel);
    return newTabPanel;
  };

  return (
    <>
      <div className="setting-tab">
        <div className="pui-tab-sec">
          <div className="dynamic-setup">
            <div
              className="description app-settings-title"
              key={currentTabKey.current + 1}
            >
              {t(localeKeyMap.settingsSelectApplication)}
            </div>
            {tabs && tabs?.length > 0 && !isLoading && (
              <Tabs
                onChange={(value) => {
                  setselectedTabName(value);
                }}
                value={selectedTabName}
              >
                {tabs?.map((tab: any) => (
                  <Tab id={`${tab?.name ?? ""}`} badge={tab?.icon ?? ""}>
                    {tab?.label ?? ""}
                  </Tab>
                ))}
              </Tabs>
            )}
            <div className="pui-wrap">
              <div
                className="description app-settings-params-title"
                key={currentTabKey.current + 1}
              >
                {t(localeKeyMap.settingsParametersTitle)}
              </div>
              {tabs &&
                tabs?.length > 0 &&
                !isLoading &&
                tabs?.map((tab: any, index: number) => (
                  <TabPanel
                    value={selectedTabName}
                    index={tab?.name ?? ""}
                    key={`${tab?.name ?? ""}-${index}`}
                  >
                    {tabPanelToShow}
                  </TabPanel>
                ))}
            </div>
          </div>
        </div>
      </div>
      <ErrorHandlingComponent
        openToast={openToastError}
        onCloseToast={handleToastBarClose}
        errorList={errorListGlobal.concat(toastErrors.current)}
        openServerError={openDialogServerError}
        onCloseServerError={onDialogForServerCloseHandler}
        openNoURL={openDialogNoURLError}
        onCloseNoURL={onDialogForNOURLCloseHandler}
        openDialogforConflict={false}
        onCloseServerConflictError={function (): void {}}
      />
      {isLoading && <Loader />}{" "}
    </>
  );
};

export function getPrductForTenant(response: any) {
  const product = response?.productId;
  return product;
}

// Get tab icons based on if error is there due to validation check or due to network error
export function getTabIcon(
  errorCount: number,
  isNetworkErrorInTab: boolean | undefined
) {
  if (errorCount > 0) {
    return <NumericBadge color="red">{errorCount}</NumericBadge>;
  } else if (isNetworkErrorInTab) {
    return <InfoIcon className="red" />;
  } else if (isNetworkErrorInTab === false && errorCount === 0) {
    return <SuccessIcon className="green" />;
  } else if (errorCount === 0) {
    return <></>;
  }
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: string;
  value: string;
}

export function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}
